import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useProjectSiteNavigation } from '@/shared/lib';
import { ConsignmentSelectionBar } from '@/features/consignment-selection';
import { ConsignmentsDataTable } from '@/widgets/consignments-data-table';

import { useConsignments } from '../lib';
import { getColumns, getColumnPresets } from '../model';
import { RouteNames } from '@/shared/config/routes';

export function ConsignmentsPage() {
  const { t } = useTranslation();
  const { navigateWithContext } = useProjectSiteNavigation();
  const {
    tableData,
    isLoading,
    isFetchingNextPage,
    error,
    isValid,
    hasMore,
    handleLoadMore,
    selectedIds,
    setSelectedIds,
    sortField,
    sortDirection,
    handleSort
  } = useConsignments();

  const columns = useMemo(() => getColumns(t), [t]);
  const columnPresets = useMemo(() => getColumnPresets(t), [t]);

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>{t('Invalid project or site parameters. Please check the URL.')}</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('Failed to load consignments. Please try again later.')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => navigateWithContext(RouteNames.CONSIGNMENT_NEW)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('cmrlist.create.title', 'Create New Consignment')}
          </Button>
        </div>

        <ConsignmentsDataTable
          data={tableData}
          columns={columns}
          title={t('cmrlist.title')}
          tableId="consignments-table"
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onClearSelection={() => setSelectedIds([])}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          enableFullscreen
          enableColumnCustomization
          enableExport
          columnPresets={columnPresets}
        />
      </div>

      <ConsignmentSelectionBar selectedIds={selectedIds} onClearSelection={() => setSelectedIds([])} />
    </>
  );
}
